import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, NgZone, OnDestroy, OnInit, output, viewChild, ViewEncapsulation } from '@angular/core';
import { GeneralProjectService } from '@app/cem/project/services/general-project.service';
import { AppProjectChangeNotificationService } from '@app/common/services/app-signal.service';
import { DataLookupService } from '@app/common/services/data-api/data-lookup.service';
import { ErrorService } from '@app/common/services/error.service';
import { EmployeeFilterComponent } from '@app/shared/filters/employee-filter/employee-filter.component';
import { MulticheckFilterComponent } from '@app/shared/filters/multicheck-filter/multicheck-filter.component';
import { PxdColumns } from '@app/shared/kendoui/grid/interfaces/pxd-columns';
import { NameValueInt } from '@app/shared/models/name-value';
import { ProjectsView } from '@app/shared/models/projects-view';
import { CellClickEvent, DataStateChangeEvent, ExcelModule, GridComponent, GridDataResult, GridModule, PageChangeEvent, RowArgs, RowClassArgs, SelectionEvent } from '@progress/kendo-angular-grid';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { NumberPipe } from '@progress/kendo-angular-intl';
import { CompositeFilterDescriptor, DataResult, DataSourceRequestState, distinct, FilterDescriptor, State } from '@progress/kendo-data-query';
import { AppStorage } from 'app/common/services/data-api/app-storage.service';
import { AssetTeam, Employee, Table } from 'app/shared/models';
import { ProjectTab } from 'app/shared/models/project/Project-Tab';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, first, takeUntil } from 'rxjs/operators';
import { default as featureFlags } from '../../../../../assets/config/feature-flags.json';
import { ProjectGridViewService } from '../../services/project-grid-view.service';
import { ProjectLandingDataService } from '../../services/project-landing-data.service';
import { ProjectTabService } from '../../services/project-tab.service';

interface SelectedGridRow {
  ProjectId: string;
  SelectedGridRow: number;
}

@Component({
  selector: 'app-project-view-list',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './project-view-list.component.html',
  styleUrls: ['./project-view-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    DatePipe,
    EmployeeFilterComponent,
    ExcelModule,
    GridModule,
    LoaderComponent,
    MulticheckFilterComponent,
    NumberPipe,
  ],
})

export class ProjectViewListComponent implements OnInit, OnDestroy {
  private projectGridViewService = inject(ProjectGridViewService);
  public projectTabService = inject(ProjectTabService);
  public projectLandingDataService = inject(ProjectLandingDataService);
  public generalProjectService = inject(GeneralProjectService);
  private app = inject(AppStorage);
  private zone = inject(NgZone);
  private lookup = inject(DataLookupService);
  private projectChangeNotify = inject(AppProjectChangeNotificationService);
  private errorService = inject(ErrorService);

  public readonly grid = viewChild(GridComponent);

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public assetSubscribe: Subscription;
  public dataStateSubscribe: Subscription;
  public appAssetTeam: AssetTeam;
  public selectedRow: SelectedGridRow = { ProjectId: '', SelectedGridRow: -1 };
  public selectedRows: SelectedGridRow[] = [];
  public pageSize: number = 10;
  public view: Observable<GridDataResult>;
  public firstDataSet: any[];
  public loading: boolean = true;
  public firstLoad: boolean = true;
  public projectsGridState: State = { skip: 0, take: 100 };
  public defaultDataState: DataSourceRequestState = { skip: 0, take: 100, group: [], sort: [{ dir: "desc", field: "ProjectId" }] };
  public state: State = { skip: 0, take: 100, sort: [{ dir: "desc", field: "ProjectId" }] };
  public selectedProjects: RowArgs[] = [];
  public selectedProjectKeys: string[] = [];
  public stateSubject = new BehaviorSubject<DataSourceRequestState>(null);
  public employeeList: Employee[] = [];
  public featureFlags = featureFlags.projects.projectHome;

  public projectTypes: string[] = [];
  public functions: string[] = [];
  public projectSubTypes: string[] = [];
  public projectStatuses: string[] = [];
  private fullProjectStatuses: NameValueInt[];
  public divisions: string[] = [];

  readonly parentLoader = output<boolean>();

  readonly onSelectedProjectsChanged = output<RowArgs[]>();

  readonly tabItems = input<ProjectTab[]>(undefined);
  readonly tabstrip = input<any>(undefined);
  readonly columns = input<PxdColumns[]>(undefined);

  constructor() {
    this.view = this.projectGridViewService;

    this.dataStateSubscribe = this.stateSubject.pipe(
      debounceTime(500))
      .subscribe(state => {
        if (!state) return;
        this.projectGridViewService.query(state)
      });
  }

  async ngOnInit() {
    this.appAssetTeam = this.app.knownStore.assetTeam;

    this.assetSubscribe = this.app.knownEvents.assetTeamSelected.subscribe(at => {
      if (this.firstLoad || (at && this.appAssetTeam !== at)) {
        this.appAssetTeam = at;
        this.refreshGrid();
      }
    });

    this.view.pipe(first(data => data !== null)).subscribe(
      (data) => {
        if (data) {
          this.loading = this.loading ? false : false;
        }

        return this.getFirst(data);
      }
    );

    this.projectGridViewService.pipe(takeUntil(this.ngUnsubscribe)).subscribe((r) => {
      this.loading = false;
    });
    this.view.pipe(takeUntil(this.ngUnsubscribe)).subscribe((r) => {
      this.parentLoader.emit(false);
    });

    this.callFilterLookups();

    this.projectChangeNotify.NotifyProjectChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(async change => {
      if (!change) return;
      this.projectGridViewService.UpdateFromNotification(change, this.fullProjectStatuses);
      this.errorService.display(`The Project Status for ${change.ProjectId} was changed and pushed to you. Change was by ${change.UpdatedBy}.`, null, false)
    });

    this.zone.onMicrotaskEmpty.subscribe({
      next: () => {
        this.zone.run(() => {
          if (this.desiredTabIndex > 0) {

            this.tabstrip().selectTab(this.desiredTabIndex);
            this.desiredTabIndex = -1;
          }
        });
      }
    });
  }

  private desiredTabIndex = -1;

  ngOnDestroy(): void {
    if (this.assetSubscribe) {
      this.assetSubscribe.unsubscribe();
    }
    if (this.dataStateSubscribe) {
      this.dataStateSubscribe.unsubscribe();
    }

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getFirst(data): void {
    if (data !== null) {
      this.firstDataSet = data.data;
      if (this.firstDataSet[0]) {
        let x = this.firstDataSet;
        this.projectLandingDataService.projectData(this.firstDataSet[0]);
      }
    }
  }

  public refreshGrid(): void {
    this.firstLoad = false;
    this.dataStateChange(this.state as DataStateChangeEvent, true);
  }

  public selectionChange(event: SelectionEvent) {
    event.selectedRows.forEach((row) => {
      this.selectedProjects.push(row);
    });

    event.deselectedRows.forEach((row) => {
      this.selectedProjects = this.selectedProjects.filter(f => f.index !== row.index);
    });

    this.onSelectedProjectsChanged.emit(this.selectedProjects);
  }

  public dataStateChange(state: DataStateChangeEvent, teamChange?: boolean): void {
    if (!this.appAssetTeam) return;
    this.loading = true;
    this.parentLoader.emit(true);
    if (!state || teamChange) {
      state = this.defaultDataState as DataStateChangeEvent;
    }

    this.state = state;
    let stateCopy = this.addCustomFilters();
    this.stateSubject.next(stateCopy);
    this.selectedRow.SelectedGridRow = -1;
  }

  private addCustomFilters() {
    let assetTeamFilter = this.lookup.buildFilter({ AssetTeamId: this.appAssetTeam.AssetTeamId }) as CompositeFilterDescriptor;
    let stateCopy = JSON.parse(JSON.stringify(this.state));

    if (stateCopy.filter && stateCopy.filter.filters) {
      stateCopy.filter.filters.map(x => {
        let currentFilter = x as FilterDescriptor;

        let toonField = `cast(ProjectId,%20%27Edm.String%27)`;
        if (currentFilter.field === 'ProjectId') {
          currentFilter.field = toonField;
          currentFilter.operator = 'contains';
          currentFilter.value = `${currentFilter.value}`;
        }
        if (currentFilter.field === 'StartDate' || currentFilter.field === 'AfeApprovalDate') {
          currentFilter.value = new Date(`${currentFilter.value}`);
        }
      });

      stateCopy.filter.filters.push(assetTeamFilter)
    } else {
      stateCopy.filter = assetTeamFilter;
    }

    return stateCopy;
  }

  private callFilterLookups(): void {
    // this.generalProjectService.getEmployees().then((x: Employee[]) => {
    //   this.employeeList = x;
    // });

    this.lookup.getNameValue(Table.ProjectTypes, null, null, true).then((x) => {
      x !== null ? this.projectTypes = x.map(m => m.name).sort() : [];
    });

    this.lookup.getNameValue(Table.Functions, null, null, true).then((x) => {
      x !== null ? this.functions = x.map(m => m.name).sort() : [];
    });

    this.lookup.getNameValue(Table.ProjectSubTypes, null, null, true).then((x) => {
      x !== null ? this.projectSubTypes = distinct(x.map(m => m.name)).sort() : [];
    });

    this.lookup.getNameValue(Table.ProjectStatuses, null, null, true).then((x) => {
      if (x != null) {
        this.projectStatuses = x.map(m => m.name).sort();
        this.fullProjectStatuses = x;
      } else {
        this.projectStatuses = [];
      }
    });

    this.lookup.getNameValue(Table.Divisions, null, null, true).then((x) => {
      x !== null ? this.divisions = x.map(m => m.name).sort() : [];
    });
  }

  public pageChange(event: PageChangeEvent): void {
    this.state.skip = event.skip;
    this.selectedRow.SelectedGridRow = -1;
  }

  private lastClickedItem: ProjectsView | null;
  public onGridCellClick(event: CellClickEvent) {
    this.lastClickedItem = event.dataItem;
    setTimeout(() => this.lastClickedItem = null, 750);
  }

  public onGridDblClick(event: MouseEvent) {
    if (!this.lastClickedItem) return;
    this.projectTabService.open(this.lastClickedItem, true, false);
  }

  public onRowClick(event, dataItem: any, rowIndex: number) {
    if (this.selectedRow.SelectedGridRow === rowIndex) {
      this.selectedRow.SelectedGridRow = -1;
    } else {
      this.selectedRow.SelectedGridRow = rowIndex;
      dataItem.WellCount = rowIndex;

      this.projectLandingDataService.projectData(dataItem);
    }
  }

  public rowCallback(context: RowClassArgs) {
    let selected: boolean = false;

    if (context.dataItem.WellCount === context.index) {
      selected = true;
    }

    return {
      clicked: selected
    };
  }

  public selectTabAsync(index: number) {
    // this will select it on the next zone refresh
    this.desiredTabIndex = index;
    this.zone.onMicrotaskEmpty.emit(null);
  }

  public selectProject(event, dataItem: ProjectsView, rowIndex: number) {
    if (event) {
      this.projectTabService.open(dataItem, true, false);
      this.tabstrip().selectTab(this.tabItems().length); //May actually need this if double clicking doesn't open the tab....
    } else {
      this.projectTabService.remove(dataItem.ProjectId, this.tabItems());
    }
  }

  public allData = (): Observable<DataResult> => {
    let customState = this.addCustomFilters();
    return this.projectGridViewService.allData(customState);
  }
}